import React, {useState} from 'react';
import {Box, Button, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Api from '../../../../store/Api';
import {
    DetailedCheckResponse,
    LlmAssessmentResponse,
    LlmCandidateState,
    LlmConfig,
} from '../../../../store/models/DuplicateCheck';
import {splitLlmCandidates} from '../../../../util/DuplicateCheckUtil';
import LlmPanel from './LlmPanel';
import LlmRawPanel from './LlmRawPanel';
import InputSummaryPanel from './InputSummaryPanel';
import CandidateCard from './CandidateCard';
import ConfigPanel from './ConfigPanel';

const useStyles = makeStyles((theme) => ({
    inputSummarySection: {
        marginBottom: theme.spacing(3),
    },
    candidatesHeader: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    llmActions: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
        marginLeft: 'auto',
    },
}));

interface DetailedResultViewProps {
    result: DetailedCheckResponse;
    llmConfig: LlmConfig | null;
    llmPreset: string | undefined;
    onLlmPresetChange: (preset: string) => void;
}

export default function DetailedResultView({result, llmConfig, llmPreset, onLlmPresetChange}: DetailedResultViewProps) {
    const classes = useStyles();

    const [llmResults, setLlmResults] = useState<Record<string, LlmCandidateState>>({});
    const [lastLlmResponse, setLastLlmResponse] = useState<LlmAssessmentResponse | null>(null);
    const [llmRunning, setLlmRunning] = useState(false);
    const [copied, setCopied] = useState(false);

    const sortedCandidates = [...result.candidates].sort((a, b) => b.totalScore - a.totalScore);
    const uncertainIds = sortedCandidates.filter((c) => c.status === 'uncertain').map((c) => c.contact.stContactID);
    const llmDisabled = llmRunning || !llmPreset;

    const setResultsFor = (ids: string[], toState: (id: string) => LlmCandidateState) =>
        setLlmResults((prev) => ({...prev, ...Object.fromEntries(ids.map((id) => [id, toState(id)]))}));

    const runLlm = async (ids: string[]) => {
        setResultsFor(ids, () => ({state: 'loading'}));
        setLlmRunning(true);
        try {
            const res = await Api.duplicateCheckLlmAssess({
                target: llmPreset ? {preset: llmPreset} : undefined,
                detailed: result,
                candidateIds: ids,
            });
            const split = splitLlmCandidates(res.assessments, ids);
            setResultsFor(ids, (id) => {
                const assessment = split[id];
                return assessment === 'missing'
                    ? {state: 'missing'}
                    : {state: 'done', assessment, model: res.target.model};
            });
            setLastLlmResponse(res);
        } catch (err: any) {
            const message = typeof err === 'string' ? err
                : Array.isArray(err) ? err.join(', ')
                    : 'Fehler beim LLM-Aufruf';
            setResultsFor(ids, () => ({state: 'error', message}));
        } finally {
            setLlmRunning(false);
        }
    };

    const copyJson = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Zwischenablage nicht verfügbar
        }
    };

    return (
        <Box mt={4}>
            <Typography variant="h6" gutterBottom>Eingabe &amp; Normalisierung</Typography>
            <Box className={classes.inputSummarySection}>
                <InputSummaryPanel input={result.input}/>
            </Box>

            <Box className={classes.candidatesHeader}>
                <Typography variant="h6">
                    {sortedCandidates.length === 0
                        ? 'Keine ähnlichen Kunden gefunden'
                        : `${sortedCandidates.length} Kandidat${sortedCandidates.length !== 1 ? 'en' : ''}`
                    }
                </Typography>
                {result.totalCandidatesFound > sortedCandidates.length && (
                    <Typography variant="body2" color="textSecondary">
                        ({result.totalCandidatesFound} gefunden, Top {sortedCandidates.length} angezeigt)
                    </Typography>
                )}
                <Button size="small" onClick={copyJson}>
                    {copied ? 'Kopiert' : 'JSON kopieren'}
                </Button>
                {sortedCandidates.length > 0 && (
                    <Box className={classes.llmActions}>
                        <LlmPanel config={llmConfig} preset={llmPreset} onPresetChange={onLlmPresetChange}/>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={uncertainIds.length === 0 || llmDisabled}
                            onClick={() => runLlm(uncertainIds)}
                        >
                            Unklare per LLM prüfen ({uncertainIds.length})
                        </Button>
                    </Box>
                )}
            </Box>

            {sortedCandidates.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                    Es wurden keine Kandidaten gefunden, die den Schwellwerten entsprechen.
                    Die Normalisierungswerte oben können helfen zu verstehen, warum.
                </Typography>
            )}

            {sortedCandidates.map((candidate, i) => {
                const id = candidate.contact.stContactID;
                return (
                    <CandidateCard
                        key={id}
                        candidate={candidate}
                        defaultExpanded={i === 0}
                        llmResult={llmResults[id]}
                        onLlmCheck={() => runLlm([id])}
                        llmDisabled={llmDisabled}
                    />
                );
            })}

            {lastLlmResponse && <LlmRawPanel response={lastLlmResponse}/>}

            {result.config?.weights && result.config?.thresholds && <ConfigPanel config={result.config}/>}
        </Box>
    );
}
